/**
 * Tells the tablet when a newer app is on the install page, so someone can update it from the
 * sign-in screen. Android lets an app download its update but not install it silently: a person
 * taps Install. Reads the page's version.json (written by scripts/release-android.mjs) when the app
 * starts and every few hours: a couple of hundred bytes each time.
 */

export interface AvailableUpdate {
  readonly version: string;
  /** The install page, opened in the browser to download it. */
  readonly url: string;
}

interface PublishedVersion {
  readonly version: string;
  readonly versionCode: number;
}

/**
 * The Android version code of a "major.minor.patch" version: must match
 * scripts/release-android.mjs, which numbers every release this way.
 */
export function versionCode(version: string): number | null {
  const match = version.match(/^(\d+)\.(\d+)\.(\d+)$/);
  if (!match) return null;
  return Number(match[1]) * 10_000 + Number(match[2]) * 100 + Number(match[3]);
}

export interface UpdateCheckOptions {
  /** The website the app is published on, e.g. https://evesix-control.pages.dev. */
  readonly siteUrl: string;
  /** This app's version, e.g. "0.5.0". */
  readonly appVersion: string;
  readonly fetch?: typeof globalThis.fetch;
  readonly intervalMs?: number;
}

export class UpdateCheck {
  readonly #siteUrl: string;
  readonly #current: number | null;
  readonly #fetch: typeof globalThis.fetch;
  readonly #intervalMs: number;
  readonly #listeners = new Set<() => void>();
  #available: AvailableUpdate | null = null;

  constructor({
    siteUrl,
    appVersion,
    fetch = (...args) => globalThis.fetch(...args),
    intervalMs = 6 * 60 * 60 * 1000,
  }: UpdateCheckOptions) {
    this.#siteUrl = siteUrl.replace(/\/+$/, '');
    this.#current = versionCode(appVersion);
    this.#fetch = fetch;
    this.#intervalMs = intervalMs;
  }

  /** A newer app on the install page; null when this one is the latest (or it is not known). */
  available(): AvailableUpdate | null {
    return this.#available;
  }

  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  /** Looks once. Without data, or for a build with no release number, nothing changes. */
  async check(): Promise<AvailableUpdate | null> {
    if (this.#current === null) return null; // a test or development build
    try {
      const res = await this.#fetch(`${this.#siteUrl}/install/version.json`, {
        cache: 'no-store',
      });
      if (!res.ok) return this.#available;
      const published = (await res.json()) as PublishedVersion;
      const next =
        published.versionCode > this.#current
          ? { version: published.version, url: `${this.#siteUrl}/install/` }
          : null;
      if (next?.version !== this.#available?.version) {
        this.#available = next;
        for (const listener of this.#listeners) listener();
      }
    } catch {
      // No data: look again next time.
    }
    return this.#available;
  }

  /** Looks now and every few hours. Returns a stop function. */
  start(): () => void {
    void this.check();
    const timer = setInterval(() => void this.check(), this.#intervalMs);
    return () => clearInterval(timer);
  }
}
